import React, { useState, useMemo, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  AdjustmentsHorizontalIcon, 
  PencilIcon, 
  TrashIcon, 
  ArrowDownIcon, 
  ArrowUpIcon,
  ArrowsUpDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../utils/supabase';
import ProductForm from './ProductForm';
import { updateProduct, inventoryApi,productsApi } from '../../utils/api';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentProductToEdit, setCurrentProductToEdit] = useState(null);

  // États pour la génération de SKU/codes-barres
  const [generatingCodesProductId, setGeneratingCodesProductId] = useState(null);
  const [codeGenerationResults, setCodeGenerationResults] = useState(null);
  const [showCodeGenerationModal, setShowCodeGenerationModal] = useState(false);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [pageSize, setPageSize] = useState(30); // Ou un autre nombre souhaité

  // Charger les produits depuis l'API backend
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm,
        // Note: 'filter' ici est pour le statut de stock (low, out) géré par getProductsForStock.
        // Si ProductList a besoin de filtrer par statut de produit (active, draft), 
        // cela nécessitera un paramètre différent ou une modification du backend.
        // Pour l'instant, nous n'envoyons pas selectedStatus ou selectedCategory au backend 
        // car getProductsForStock n'est pas conçu pour eux.
        // Le filtrage par category/status se fera côté client pour l'instant.
      };

      // Utiliser productApi.getProductsForCatalog qui est déjà configuré pour la pagination
      const response = await productsApi.getProductsForCatalog(params);

      if (response && response.data) {
        // Les produits sont formatés par le backend pour le contexte de StockManagement.
        // Il faudra peut-être ajuster le formatage si ProductList a des besoins différents
        // ou créer une route API dédiée pour ProductList.
        // Pour l'instant, on utilise les données telles quelles.
        setProducts(response.data.products || []);
        setTotalProducts(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 0);
      } else {
        throw new Error("Réponse invalide de l'API");
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      setError(error.response?.data?.message || error.message || 'Erreur de chargement des produits.');
      setProducts([]);
      setTotalProducts(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, pageSize]); // Dépendances pour recharger

  // Gérer le changement de page
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Filtrage des produits (côté client pour selectedCategory et selectedStatus pour l'instant)
  const filteredProducts = useMemo(() => {
    let productsToFilter = [...products]; // products vient maintenant de l'API (déjà filtré par searchTerm backend)
    
    if (selectedCategory) {
      productsToFilter = productsToFilter.filter(product => product.category === selectedCategory);
    }
    if (selectedStatus) {
      // Assumons que les produits de l'API ont un champ 'status' correspondant (active, draft, etc.)
      // Si le format du backend pour `getProductsForStock` ne retourne pas ce statut de produit,
      // il faudra l'ajouter au backend ou le déduire différemment.
      productsToFilter = productsToFilter.filter(product => product.status_product === selectedStatus); // Supposons un champ 'status_product'
    }
    return productsToFilter;
  }, [products, selectedCategory, selectedStatus]);

  // Tri des produits
  const sortedProducts = useMemo(() => {
    let sortableItems = [...filteredProducts];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredProducts, sortConfig]);

  // Liste des catégories uniques
  const categories = useMemo(() => {
    const uniqueCategories = new Set(products.map(product => product.category));
    return ['', ...Array.from(uniqueCategories)];
  }, [products]);

  // Gestion du tri
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Rendu des icones de tri
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 text-primary-500" />
      : <ArrowDownIcon className="h-4 w-4 text-primary-500" />;
  };

  // Fonction utilitaire pour tronquer le texte
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Suppression d'un produit
  const handleDeleteProduct = async (id) => {
    try {
      // Supprimer d'abord les variants liés au produit
      const { error: variantsError } = await supabase
        .from('product_variants')
        .delete()
        .eq('product_id', id);
      
      if (variantsError) throw variantsError;
      
      // Puis supprimer le produit
      const { error: productError } = await supabase
        .from('products')
        .delete()
        .eq('id', id);
      
      if (productError) throw productError;
      
      // Mettre à jour l'état local
      setProducts(products.filter(product => product.id !== id));
      setShowDeleteConfirm(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Erreur lors de la suppression du produit:', error);
      // Afficher un message d'erreur à l'utilisateur
      alert(`Erreur: ${error.message}`);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // --- Fonctions pour l'édition --- 
  const handleOpenEditModal = (product) => {
    console.log("Editing product:", product);
    setCurrentProductToEdit(product);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setCurrentProductToEdit(null);
  };

  const handleUpdateProduct = async (updatedProductData) => {
    if (!currentProductToEdit) return;
    
    console.log("Données à sauvegarder pour l'update:", updatedProductData);

    try {
      // 1. Appeler l'API pour la mise à jour
      const result = await updateProduct(currentProductToEdit.id, updatedProductData);
      console.log("Réponse API Update:", result);

      // 2. Mettre à jour l'état local `products`
      // Il est crucial de bien reconstruire l'objet produit pour la liste
      // avec les données renvoyées ou, à défaut, avec `updatedProductData`
      // et en s'assurant que les variations sont aussi à jour.
      // C'est souvent la partie la plus délicate.
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === currentProductToEdit.id 
          ? { 
              ...p, // Conserver les champs non modifiés (comme l'ID numérique, etc.)
              ...updatedProductData, // Appliquer les données mises à jour
              // Assurez-vous que les variations sont au bon format pour l'affichage liste
              variants: (updatedProductData.variants || []).map(v => ({
                  id: v.id,
                  name: v.title,
                  stock: v.inventory_quantity,
                  sku: v.sku,
                  price: v.price,
                  image: v.image_url,
                  options: v.options,
                  discountPrice: v.compare_at_price,
                  barcode: v.barcode
              })),
              // Recalculer le prix/stock principal si nécessaire pour l'affichage liste
              price: updatedProductData.has_variants 
                     ? (updatedProductData.variants[0]?.price || 0) 
                     : updatedProductData.price,
              discountPrice: updatedProductData.has_variants 
                     ? (updatedProductData.variants[0]?.compare_at_price || null) 
                     : updatedProductData.discountPrice,
              stockLevel: updatedProductData.has_variants
                    ? updatedProductData.variants.reduce((sum, v) => sum + v.inventory_quantity, 0)
                    : updatedProductData.stockLevel
            } 
          : p
        )
      );

      // 3. Fermer le modal
      handleCloseEditModal();

    } catch (error) {
      console.error("Erreur lors de la mise à jour du produit:", error);
      // Afficher l'erreur à l'utilisateur (peut-être dans le modal ProductForm via un prop ?)
      alert(`Erreur: ${error.message}`);
      // Ne pas fermer le modal en cas d'erreur pour que l'utilisateur puisse corriger
    }
  };
  // --- Fin Fonctions pour l'édition ---

  // **NOUVELLE FONCTION : Générer les SKU et codes-barres**
  const handleGenerateSkuAndBarcode = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) {
      alert("Produit non trouvé.");
      return;
    }

    setGeneratingCodesProductId(productId);
    try {
      console.log('[ProductList] Génération SKU/Barcode pour produit:', productId);
      
      const response = await inventoryApi.generateSkuAndBarcode({ 
        productId: productId 
      });
      
      if (response && response.data && response.data.success) {
        const data = response.data.data;
        
        // Stocker les résultats pour affichage dans le modal
        setCodeGenerationResults({
          success: true,
          message: response.data.message,
          productName: product.name,
          data: data
        });
        setShowCodeGenerationModal(true);
        
        // Recharger les produits pour afficher les nouveaux codes
        await fetchProducts(); 
        
        console.log('[ProductList] Génération codes terminée:', data);
      } else {
        throw new Error(response?.data?.message || "La génération des codes a échoué. Réponse inattendue du serveur.");
      }
    } catch (error) {
      console.error(`[ProductList] Erreur lors de la génération des codes pour le produit ${productId}:`, error);
      
      // Stocker l'erreur pour affichage dans le modal
      setCodeGenerationResults({
        success: false,
        message: `Erreur de génération: ${error.message}`,
        productName: product.name,
        data: null
      });
      setShowCodeGenerationModal(true);
    } finally {
      setGeneratingCodesProductId(null);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg">
      {/* Barre de recherche et filtres */}
      <div className="p-4 border-b border-gray-200 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder="Rechercher par nom ou référence..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div>
          <select
            className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Toutes les catégories</option>
            {categories.slice(1).map((category, index) => (
              <option key={index} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div>
          <select
            className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="draft">Brouillon</option>
          </select>
        </div>
      </div>

      {/* Afficher un indicateur de chargement */}
      {loading && (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-2 text-gray-500">Chargement des produits...</p>
        </div>
      )}
      
      {/* Afficher un message d'erreur */}
      {error && (
        <div className="p-8 text-center text-red-500">
          <p>Erreur: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-primary-500 text-white rounded-md"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Tableau de produits */}
      {!loading && !error && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('name')}
                  >
                    Produit
                    <span className="ml-2">{getSortIcon('name')}</span>
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('category')}
                  >
                    Catégorie
                    <span className="ml-2">{getSortIcon('category')}</span>
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('price')}
                  >
                    Prix
                    <span className="ml-2">{getSortIcon('price')}</span>
                  </button>
                </th>

                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button 
                    className="group inline-flex items-center"
                    onClick={() => requestSort('status')}
                  >
                    Statut
                    <span className="ml-2">{getSortIcon('status')}</span>
                  </button>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img className="h-10 w-10 rounded-md object-cover" src={product.mainImage} alt={product.name} />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {truncateText(product.name)}
                        </div>
                        <div className="text-sm text-gray-500">{product.sku}</div>
                        <div className="text-sm text-gray-500">{product.barcode ? product.barcode : product.variants && product.variants.length > 0 && product.variants[0].barcode ? product.variants[0].barcode : ''}</div>

                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{product.category}</div>
                    <div className="text-sm text-gray-500">{product.subCategory}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    { product.discountPrice && product.discountPrice > 0 ? (
                      <div>
                        <div className="text-sm font-medium text-gray-900">{formatPrice(product.discountPrice)} XOF</div>
                        <div className="text-sm text-gray-500 line-through">{formatPrice(product.price)} XOF</div>
                      </div>
                    ) : product.price && product.price > 0 ? (
                      <div className="text-sm text-gray-900">{formatPrice(product.price)} XOF</div>
                    ) : product.variants && product.variants.length > 0 && product.variants[0].price > 0 ? (
                      <div className="text-sm text-gray-900">{formatPrice(product.variants[0].price)} XOF</div>
                    ) : (
                      <span className="text-sm text-gray-500">N/A</span>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    {
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${product.status === 'active' ? 'bg-green-100 text-green-800' : product.status === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {product.status === 'active' ? 'Actif' : product.status === 'inactive' ? 'Inactif' : 'Brouillon'}
                      </span>
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button 
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowQuickView(true);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                        title="Voir les détails"
                      >
                        <EyeIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        onClick={() => handleGenerateSkuAndBarcode(product.id)}
                        disabled={generatingCodesProductId === product.id}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Générer les SKU et codes-barres"
                      >
                        {generatingCodesProductId === product.id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green-600"></div>
                        ) : (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a.997.997 0 01-1.414 0l-7-7A2 2 0 013 12V7a2 2 0 012-2z" />
                          </svg>
                        )}
                      </button>
                      <button 
                        onClick={() => product.external_source === 'local' && handleOpenEditModal(product)}
                        className={`
                          ${product.external_source === 'local' 
                            ? 'text-primary-600 hover:text-primary-900' 
                            : 'text-gray-400 cursor-not-allowed'}
                        `}
                        title={product.external_source === 'local' ? "Modifier le produit" : "Les produits importés ne peuvent pas être modifiés ici"}
                        disabled={product.external_source !== 'local'}
                      >
                        <PencilIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowDeleteConfirm(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer le produit"
                      >
                        <TrashIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Message si aucun produit */}
          {sortedProducts.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              <p>Aucun produit trouvé. Importez des produits depuis Shopify ou créez-en de nouveaux.</p>
            </div>
          )}

          {/* Contrôles de Pagination */}
          {totalPages > 0 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Précédent
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{totalPages}</span> ({totalProducts} produits)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Précédent</span>
                      <ArrowDownIcon className="h-5 w-5 transform -rotate-90" aria-hidden="true" /> {/* Correction icône pour Précédent */}
                    </button>
                    {/* Potentiellement ajouter des numéros de page ici */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <span className="sr-only">Suivant</span>
                      <ArrowUpIcon className="h-5 w-5 transform -rotate-90" aria-hidden="true" /> {/* Correction icône pour Suivant */}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vue rapide du produit */}
      {showQuickView && selectedProduct && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-start">
              <h3 className="text-lg font-medium text-gray-900">
                {truncateText(selectedProduct.name)}
              </h3>
              <button
                onClick={() => {
                  setShowQuickView(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Fermer</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <img className="w-full h-48 object-cover rounded-md" src={selectedProduct.mainImage} alt={selectedProduct.name} />
                <div className="mt-4">
                  <p className="text-sm text-gray-500">SKU: {selectedProduct.sku}</p>
                  <p className="mt-1 text-lg font-semibold">
                    { selectedProduct.discountPrice ? (
                      <>
                        <span className="text-primary-600">{formatPrice(selectedProduct.discountPrice)} XOF</span>
                        <span className="ml-2 text-gray-500 line-through">{formatPrice(selectedProduct.price)} XOF</span>
                      </>
                    ) : (
                      <span>{formatPrice(selectedProduct.price)} XOF</span>
                    )}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-medium">Détails</h4>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="text-gray-500">Catégorie:</div>
                  <div>{selectedProduct.category}</div>
                  <div className="text-gray-500">Sous-catégorie:</div>
                  <div>{selectedProduct.subCategory}</div>
                  <div className="text-gray-500">Statut:</div>
                  <div>
                    {
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedProduct.status === 'active' ? 'bg-green-100 text-green-800' : selectedProduct.status === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {selectedProduct.status === 'active' ? 'Actif' : selectedProduct.status === 'inactive' ? 'Inactif' : 'Brouillon'}
                      </span>
                    }
                  </div>
                  <div className="text-gray-500">Stock:</div>
  
                </div>

                {selectedProduct.variants.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium">variants</h4>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {selectedProduct.variants.map(variation => (
                        <div key={variation.id} className="border rounded p-2 text-sm">
                          <div className="flex justify-between">
                            <span>{variation.name}</span>
                            <span className={
                              variation.stock <= 0 ? 'text-red-600' : 
                              variation.stock < 3 ? 'text-yellow-600' : 
                              'text-green-600'
                            }>
                              Stock: {variation.stock}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowQuickView(false);
                  setSelectedProduct(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Fermer
              </button>
              <button
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
              >
                Éditer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && selectedProduct && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Supprimer le produit</h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Êtes-vous sûr de vouloir supprimer "{truncateText(selectedProduct.name)}" ? Cette action ne peut pas être annulée.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => handleDeleteProduct(selectedProduct.id)}
              >
                Supprimer
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedProduct(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition (Nouveau) */}
      {showEditModal && currentProductToEdit && (
        <ProductForm 
          product={currentProductToEdit}
          onClose={handleCloseEditModal}
          onSave={handleUpdateProduct}
        />
      )}

      {/* Modal de résultats de génération de codes */}
      {showCodeGenerationModal && codeGenerationResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* En-tête du modal */}
            <div className={`px-6 py-4 border-b ${codeGenerationResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {codeGenerationResults.success ? (
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                  <h3 className={`ml-3 text-lg font-semibold ${codeGenerationResults.success ? 'text-green-900' : 'text-red-900'}`}>
                    {codeGenerationResults.success ? 'Codes générés avec succès !' : 'Erreur de génération'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowCodeGenerationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenu du modal */}
            <div className="px-6 py-4">
              {codeGenerationResults.success && codeGenerationResults.data ? (
                <div className="space-y-6">
                  {/* Informations du produit */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📦 Produit traité</h4>
                    <p className="text-blue-800">{codeGenerationResults.productName}</p>
                  </div>

                  {/* Résumé principal */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {codeGenerationResults.data.totalUpdated || 0}
                      </div>
                      <div className="text-sm text-green-800">Éléments mis à jour</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {codeGenerationResults.data.updatedItems?.length || 0}
                      </div>
                      <div className="text-sm text-blue-800">Codes générés</div>
                    </div>
                  </div>

                  {/* Détails des éléments mis à jour */}
                  {codeGenerationResults.data.updatedItems && codeGenerationResults.data.updatedItems.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">🏷️ Codes générés</h4>
                      <div className="space-y-3">
                        {codeGenerationResults.data.updatedItems.map((item, index) => (
                          <div key={index} className="bg-white rounded-md p-3 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-900">
                                {item.type === 'product' ? '📦' : '🔗'} {item.name}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.type === 'product' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                              }`}>
                                {item.type === 'product' ? 'Produit' : 'Variante'}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="font-medium text-gray-600">SKU:</span>
                                <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">{item.sku}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-600">Code-barres:</span>
                                <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">{item.barcode}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Erreurs */}
                  {codeGenerationResults.data.errors && codeGenerationResults.data.errors.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-2">
                        ⚠️ Erreurs ({codeGenerationResults.data.errors.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto">
                        {codeGenerationResults.data.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-700 mb-1">
                            • {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-red-600">{codeGenerationResults.message}</p>
                </div>
              )}
            </div>

            {/* Pied du modal */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowCodeGenerationModal(false)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList; 